/**
 * @file        src/lib/promo/promoConfig.ts
 * @description Single source of truth for the current JCK AUTO promo offer, plus the pure
 *              function that says whether it is running. The window, the button, the display
 *              logic and the public terms page all read this file, so the offer on screen and
 *              the terms published to visitors cannot disagree.
 * @universal   knowledge/universals/engines.md#promoconfig
 * @usage import { PROMO_PROGRAM, isPromoActive } from "@/lib/promo/promoConfig";
 *
 * @rule        No React, no JSX, no DOM, no fetch, no filesystem. This module is imported from
 *              server components, client components and plain node scripts alike.
 * @rule        NOTHING about the offer comes from the environment. The start date and the copy
 *              are configuration IN CODE, reviewed in a pull request — never an environment
 *              variable that can be changed without a diff. The single `process.env` read below
 *              is `NODE_ENV`, and only to keep the wording guard out of production; it never
 *              influences what the offer IS.
 * @rule        The offer is ONE object. Do not add a registry of several programs or a second
 *              config file for the next offer: the next offer is a new object of the same shape.
 *              If several offers must ever run at once, that is an explicit decision of its own.
 * @rule        PERMANENT SINCE 2026-10-20 (owner decision, ADR N-PROMO-PERMANENT-1). The offer
 *              has a start date and NO end date, NO quantity and NO counter. Do not bring back a
 *              daily schedule or a "N of M" number without a new owner decision: a number on
 *              screen is a factual claim that needs a published schedule behind it (38-FZ).
 */

// ─── TYPES ───

/** The photograph shown inside the offer window. */
export interface PromoImage {
  /** Path to the committed JPEG master under public/. */
  src: string;
  /** Concrete alt text — the photo depicts the product, so it is not decorative. */
  alt: string;
}

/**
 * Every visible word of the window and the button. Nothing may be retyped in a component:
 * a string typed at a call site is a second source of truth that will drift from the terms
 * page, and the terms page is the legal document. The terms themselves live on that page.
 */
export interface PromoTexts {
  heading: string;
  ctaLabel: string;
  buttonLabel: string;
  rulesLinkLabel: string;
}

/** The shape every future offer reuses. A new offer is a new object, not new code. */
export interface PromoProgram {
  /** Stable slug for the offer. */
  id: string;
  /** Lead `source` label used by the form inside the window. Needs a matching Metrika goal. */
  sourceLabel: string;
  /** First day of the offer, YYYY-MM-DD, or null when the program is off. */
  startDate: string | null;
  /** IANA zone whose midnight is the day boundary for every visitor. */
  timeZone: string;
  /** Public terms page. Required in every render of the window (art. 9, 38-FZ). */
  rulesHref: string;
  image: PromoImage;
  texts: PromoTexts;
}

// ─── PROGRAM ───

/**
 * The current offer.
 *
 * @rule `startDate` is the ONE switch. Null means off; a date means the offer runs from that
 *       calendar date in `timeZone` until the date is set back to null. There is no end date:
 *       the offer is permanent until the owner cancels it.
 * @rule The `sourceLabel` and the `id` are kept from the August 2026 run on purpose: the lead
 *       source feeds the existing Metrika goal `lead_promo_eva`, so reports continue unbroken.
 */
export const PROMO_PROGRAM: PromoProgram = {
  id: "eva-mats-2026-08",
  sourceLabel: "promo-eva-mats",
  startDate: "2026-10-20",
  timeZone: "Asia/Vladivostok",
  rulesHref: "/promo/eva-kovriki",
  image: {
    src: "/images/promo/eva-interior.jpg",
    alt: "Салон автомобиля с комплектом EVA-ковриков",
  },
  texts: {
    heading: "Комплект ковриков на авто в подарок при заказе авто через сайт",
    ctaLabel: "Получить подарок",
    buttonLabel: "Подарок",
    rulesLinkLabel: "Условия акции",
  },
};

// ─── VALIDATION ───

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Check a YYYY-MM-DD string for real calendar validity, not just shape.
 * "2026-02-31" matches the regex but is not a date.
 */
function isRealIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return (
    probe.getUTCFullYear() === y &&
    probe.getUTCMonth() === m - 1 &&
    probe.getUTCDate() === d
  );
}

/**
 * Validate the program on module load.
 *
 * @param program the configured offer
 * @returns the failure reason, or null when the config is sound
 */
function findConfigFault(program: PromoProgram): string | null {
  if (program.startDate !== null && !isRealIsoDate(program.startDate)) {
    return `startDate must be null or a real YYYY-MM-DD date, got ${JSON.stringify(program.startDate)}`;
  }
  return null;
}

// @rule A broken config degrades to "program off" and NEVER throws at import time. This module is
// imported by page-level components; a throw here takes down whichever page imports it. The
// reason is logged exactly once, on load, so a misconfiguration is loud in the server log while
// staying invisible and harmless to the visitor.
const CONFIG_FAULT: string | null = findConfigFault(PROMO_PROGRAM);

if (CONFIG_FAULT !== null) {
  console.error(`[promoConfig] program disabled — invalid config: ${CONFIG_FAULT}`);
}

// ─── WORDING GUARD (И-1) ───

// @rule This guard is why false-scarcity framing cannot quietly come back. The offer has no
// quantity at all, so any word implying that other buyers are taking the gifts is a false claim
// about the market: art. 5 of federal law 38-FZ, fined 100–500 thousand roubles under art. 14.3
// of the administrative code, and provable straight from the page source. A review checklist
// forgets; this list does not, which is the entire point of making the invariant executable.
// @rule Development only. It must never cost anything on a live page, and a copy
// mistake caught in review is caught before production by definition.
const FORBIDDEN_WORDING: readonly string[] = Object.freeze([
  "осталось",
  "остал",
  "разбирают",
  "успей",
  "успейте",
  "последн",
  "уже забрал",
  "спешите",
]);

/**
 * Report any promo copy that implies other buyers consumed the gifts.
 *
 * Exported so the same check can run from a test or a script, not only on import.
 *
 * @param texts the copy block to inspect
 * @returns one entry per offending key, empty when the copy is clean
 */
export function findForbiddenWording(
  texts: PromoTexts,
): { key: string; match: string }[] {
  const found: { key: string; match: string }[] = [];
  for (const [key, value] of Object.entries(texts)) {
    const haystack = String(value).toLowerCase();
    for (const bad of FORBIDDEN_WORDING) {
      if (haystack.includes(bad)) found.push({ key, match: bad });
    }
  }
  return found;
}

if (process.env.NODE_ENV !== "production") {
  for (const hit of findForbiddenWording(PROMO_PROGRAM.texts)) {
    console.error(
      `[promoConfig] forbidden wording in texts.${hit.key}: "${hit.match}" — ` +
        "the offer must never be framed as running out (И-1)",
    );
  }
}

// ─── DATE ───

/**
 * The calendar date at `now` as seen in `timeZone`, formatted YYYY-MM-DD.
 *
 * @param now the instant to resolve
 * @param timeZone IANA zone
 * @returns the local calendar date, or null if the zone is unusable
 */
function calendarDateIn(now: Date, timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const y = get("year");
    const m = get("month");
    const d = get("day");
    if (!y || !m || !d) return null;
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

// ─── PUBLIC API ───

/**
 * Whether the offer is running at `now`.
 *
 * @input   now — the moment to evaluate, defaulting to a fresh Date so callers can pin the clock
 * @output  true from `startDate` (in `timeZone`) onwards; false when off or the config is broken
 * @important YYYY-MM-DD strings compare correctly as plain strings, so no instant arithmetic is
 *            needed — the visitor's device clock zone never matters (И-2).
 */
export function isPromoActive(now: Date = new Date()): boolean {
  if (CONFIG_FAULT !== null) return false;
  const { startDate, timeZone } = PROMO_PROGRAM;
  if (startDate === null) return false;

  const today = calendarDateIn(now, timeZone);
  if (today === null) return false;
  return today >= startDate;
}
